import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import Icon, { XButton } from "./ui-elements/Icon";
import { isValidObject, possessiveNoun } from "../utils/functions";
import Countdown from "./data-display/Countdown";

type Props = {
  error: any;
  onClick: (response: any) => void;
  clientName: string;
  stopDate: any;
  isSubmitting: boolean;
  onCancelDecline: () => void;
};

const DeclineRequest = observer(
  ({
    error,
    onClick,
    clientName,
    stopDate,
    isSubmitting,
    onCancelDecline = () => {},
    ...rest
  }: Props) => {
    const [response, setResponse] = React.useState({ reason: "", comment: "" });
    const [declineInvalid, setDeclineInvalid] = React.useState(false);

    const handleClick = () => {
      if (isValidObject(response)) {
        setDeclineInvalid(false);
        onClick(response);
      } else {
        setDeclineInvalid(true);
      }
    };

    const { isOpen, onOpen, onClose } = useDisclosure();

    const declineReasons = [
      {
        label: "I'm unavailable for this schedule",
        value: "Schedule",
      },
      {
        label: "The price doesn't work for me",
        value: "Pricing",
      },
      {
        label: "My skills don't fit the client's needs",
        value: "Expertise",
      },
      {
        label: "I can't go to the client's location",
        value: "Location",
      },
    ];

    return (
      <Flex
        height="100%"
        direction="column"
        justifyContent="space-between"
        {...rest}
      >
        <Stack spacing={[5, 6]}>
          <Alert
            status="warning"
            variant="subtle"
            rounded="fbRadius"
            flexDirection="column"
            justifyContent="center"
            textAlign="center"
            py={6}
          >
            <AlertIcon h={[6, 8]} w={[6, 8]} />
            <AlertTitle mt={4} mb={2} fontSize="lg">
              You may not get more clients
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              <Text mb={2}>
                If you decline up to 2 requests, your account may be suspended.
              </Text>
              <Link
                onClick={() => (isOpen ? onClose() : onOpen())}
                color="blue.500"
              >
                {isOpen ? "Hide this" : "Here's why"}
              </Link>

              <Collapse in={isOpen}>
                <Stack mt={"2"} textAlign="start">
                  <Text>
                    Declining requests often leads to a bad experience and
                    losing clients which costs us a lot of time and money to
                    manage.
                  </Text>

                  <Text as="i">
                    To avoid receiving requests you may have to decline, please
                    ensure your availability, subjects and prices are always
                    up-to-date.
                  </Text>

                  <Text>
                    If you decline 2 requests within any 4 months period,
                    clients will hardly see your profile in search results.
                  </Text>

                  <Text>
                    If you decline 3 requests in the same period, your account
                    will be suspended from receiving any clients for the next 4
                    months.
                  </Text>

                  <Text>
                    Also, you must respond to requests within 24 hours,
                    otherwise we'll assume you're unavailable and your account
                    will be suspended from getting new requests.
                  </Text>
                </Stack>
              </Collapse>
            </AlertDescription>
          </Alert>

          <FormControl isInvalid={declineInvalid && !Boolean(response.reason)}>
            <FormLabel mb={2}>
              Why are you declining {possessiveNoun(clientName)} request?
            </FormLabel>
            <RadioGroup
              size="lg"
              onChange={(value) => setResponse({ ...response, reason: value })}
              value={response.reason}
            >
              <Stack spacing={3}>
                {declineReasons.map((reason) => (
                  <Radio
                    key={reason.value}
                    isInvalid={declineInvalid && !Boolean(response.reason)}
                    value={reason.value}
                  >
                    <Text fontSize="md">{reason.label}</Text>
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
            <FormErrorMessage>Please select a reason</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={declineInvalid && !Boolean(response.comment)}>
            <FormLabel mb={2}>Explain your reason</FormLabel>
            <Textarea
              resize="none"
              height={[24, 32]}
              value={response.comment}
              placeholder="Please be descriptive. This will be read by Tuteria staff"
              onChange={(e) =>
                setResponse({ ...response, comment: e.target.value })
              }
            />
            <FormErrorMessage>Explain why you're declining</FormErrorMessage>
          </FormControl>
        </Stack>

        <Stack py={[5, 6]} align="center">
          {error && (
            <Text color="red.500" fontSize="sm">
              An error occurred. Please try again
            </Text>
          )}

          <Flex width="100%">
            <Button
              flex={1}
              size="lg"
              onClick={onCancelDecline}
              variant="outline"
            >
              Cancel
            </Button>

            <Box boxSize={[3, 5]} />

            <Button
              flex={1}
              size="lg"
              loadingText="Submitting"
              onClick={handleClick}
              colorScheme="blue"
              isLoading={isSubmitting}
            >
              Decline
            </Button>
          </Flex>

          <Stack isInline mt={2} align="center">
            <Icon name="passing-time" color="red.500" h="4" w="4" />
            <Box fontSize="sm" pr={1}>
              <Countdown date={stopDate} variant="plain" /> left to respond
            </Box>
          </Stack>
        </Stack>
      </Flex>
    );
  },
);

export default DeclineRequest;
